/**
 * ScoreBreakdownSection — 8-category AI scorecard rendered at the top of
 * the AI Insights modal.
 *
 * Data shape (matches backend services/score_breakdown.py):
 *   {
 *     degree:              { score: int 0-10, severity: 'critical|important|irrelevant', explanation: '...' },
 *     years_of_experience: { ... },
 *     must_have_skills:    { ... },
 *     good_to_have_skills: { ... },
 *     transferable_skills: { ... },
 *     relevant_experience: { ... },
 *     subjective_fit:      { ... },
 *     domain_relevance:    { ... },
 *   }
 *
 * Props:
 *   scoreBreakdown — the dict above, OR null/undefined for old candidates
 *                    uploaded before this feature shipped. When null/empty
 *                    the component renders NOTHING — never crashes the
 *                    parent and leaves the existing AI Insights modal
 *                    untouched.
 *
 * Layout (current — "top score strip" redesign):
 *   * Horizontal grid of tiles, 4 per row on lg+ (2 per row on md,
 *     1 per row below). Each tile is super compact:
 *       - severity color stripe across the top edge (red/amber/emerald)
 *       - category name (1 line, truncated)
 *       - big score number + severity chip on a single row
 *       - mini progress bar
 *       - "Why?" toggle that expands an inline explanation
 *     The grid uses items-start so an expanded tile only grows its own
 *     row — it never stretches sibling tiles.
 *
 * Design choices:
 *   * Self-contained — no global styles, no context. Drop into any modal /
 *     report page.
 *   * Tolerant — missing categories are skipped (some category-specific
 *     entries may have failed AI validation). Score = 0 is rendered
 *     correctly (not hidden as falsy).
 *   * The category ORDER is fixed here, not pulled from the data, so the
 *     UI always presents the same top-to-bottom flow regardless of which
 *     keys the backend returns.
 */

import React, { useState } from 'react';

// Canonical order — top to bottom in the rendered section.
// Mirrors services/score_breakdown.py CATEGORIES but kept here to avoid
// a runtime backend round-trip just to learn the order.
const CATEGORY_ORDER = [
  'degree',
  'years_of_experience',
  'must_have_skills',
  'good_to_have_skills',
  'transferable_skills',
  'relevant_experience',
  'subjective_fit',
  'domain_relevance',
];

const CATEGORY_LABELS = {
  degree:              'Degree',
  years_of_experience: 'Years of Exp',
  must_have_skills:    'Must-have',
  good_to_have_skills: 'Good-to-have',
  transferable_skills: 'Transferable',
  relevant_experience: 'Relevant Exp',
  subjective_fit:      'Subjective Fit',
  domain_relevance:    'Domain',
};

// Longer labels used in tooltips + the older CompactScoreBreakdown view —
// the short CATEGORY_LABELS above are tuned for narrow tile widths where
// "Years of Experience" wraps awkwardly; the long names live here so the
// information isn't lost.
const CATEGORY_LABELS_LONG = {
  degree:              'Degree',
  years_of_experience: 'Years of Experience',
  must_have_skills:    'Must-have skills',
  good_to_have_skills: 'Good-to-have skills',
  transferable_skills: 'Transferable skills',
  relevant_experience: 'Relevant experience',
  subjective_fit:      'Subjective fit',
  domain_relevance:    'Domain relevance',
};

// Severity → chip styling. Monochrome / neutral by design so the tiles
// read as a calm, professional grid (no colored "alerts" competing with
// the score numbers for attention). The chip text still differentiates
// critical/important/irrelevant — color is just not the carrier of that
// meaning here.
const SEVERITY_STYLES = {
  critical:   'bg-slate-100 text-slate-700 border-slate-200',
  important:  'bg-slate-100 text-slate-700 border-slate-200',
  irrelevant: 'bg-slate-100 text-slate-500 border-slate-200',
};

const SEVERITY_LABELS = {
  critical:   'Critical',
  important:  'Important',
  irrelevant: 'Irrelevant',
};

// Bar fill — gradient by score (red → orange → amber → emerald). This
// is one of the FEW colored elements kept in the tile design because
// the color carries genuine information: a recruiter glancing at the
// strip can spot weak vs strong scores by bar color without having to
// read each number. Severity chips stay neutral (their meaning is
// already in the label text), but bars stay colored.
const _barColor = (score) => {
  const s = Number(score) || 0;
  if (s >= 8) return 'bg-emerald-500';
  if (s >= 5) return 'bg-amber-500';
  if (s >= 3) return 'bg-orange-500';
  return 'bg-red-500';
};

/**
 * Compact tile representing one scored category in the horizontal grid.
 *
 * Layout (clean monochrome — no colored severity stripe, no colored
 * bar fill; severity is communicated via the chip text only):
 *   ┌────────────────────────────────────┐
 *   │ Category Name                      │ ← truncates if too long
 *   │ 8/10              [Important]      │ ← score + severity chip row
 *   │ ▓▓▓▓▓▓▓▓░░                         │ ← neutral slate bar fill
 *   │ Why? ▼                             │ ← only if explanation exists
 *   │ (expanded body)                    │ ← only when toggled open
 *   └────────────────────────────────────┘
 *
 * The whole tile is NOT click-to-expand (would be ambiguous with the
 * explicit Why? affordance). Only the Why? button toggles the body.
 */
function ScoreTile({ category, entry, expanded, onToggle }) {
  // Prefer the long, human-friendly label; fall back to the short
  // tile-tuned label, then to the raw category key. The tile is wide
  // enough in this design (no longer a tiny sidebar slot) that the
  // long label fits on one line for nearly all categories.
  const fullLabel =
    CATEGORY_LABELS_LONG[category] || CATEGORY_LABELS[category] || category;
  const score = Number(entry?.score) || 0;
  const severity = entry?.severity || 'irrelevant';
  const severityCls = SEVERITY_STYLES[severity] || SEVERITY_STYLES.irrelevant;
  const severityLabel = SEVERITY_LABELS[severity] || severity;
  const explanation = (entry?.explanation || '').trim();

  // Bar width as a percentage of 10. Clamp defensively in case the AI
  // returned a value outside 0-10 (validator clamps, but cheap insurance
  // against future schema drift).
  const barPct = Math.max(0, Math.min(100, (score / 10) * 100));

  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden transition-shadow hover:shadow-sm">
      <div className="px-2.5 py-2">
        {/* Category name — uses the FULL label, truncated on overflow,
            full name in title attribute for hover. */}
        <div
          className="text-[11px] font-semibold text-slate-700 truncate"
          title={fullLabel}
        >
          {fullLabel}
        </div>

        {/* Score + severity chip on one line. Score uses big-ish font so
            it's the dominant element on the tile when scanning. */}
        <div className="mt-1 flex items-baseline justify-between gap-1.5">
          <span className="text-lg font-bold tabular-nums text-slate-900 leading-none">
            {score}
            <span className="text-xs font-medium text-slate-400">/10</span>
          </span>
          <span
            className={
              'inline-flex items-center text-[9px] font-semibold px-1.5 py-0 rounded border ' +
              severityCls
            }
            title={`This category is ${severityLabel.toLowerCase()} for this role.`}
          >
            {severityLabel}
          </span>
        </div>

        {/* Mini progress bar — score-gradient fill (red/orange/amber/
            emerald). The color is intentional: it lets recruiters spot
            weak vs strong scores at a glance without reading the digit.
            Severity stays neutral elsewhere; only the bar uses color. */}
        <div className="mt-1.5 h-1 w-full rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full ${_barColor(score)} transition-[width] duration-300`}
            style={{ width: `${barPct}%` }}
          />
        </div>

        {/* Why? toggle — only renders when there's an explanation to
            show. Keeps short-explanation tiles from carrying a dead
            button. The button looks like a link rather than a chunky
            button so it doesn't compete with the score for attention. */}
        {explanation && (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="mt-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:underline"
          >
            {expanded ? 'Hide' : 'Why?'}
            <span className="text-[8px]" aria-hidden="true">
              {expanded ? '▲' : '▼'}
            </span>
          </button>
        )}

        {/* Expanded explanation — renders inline inside the tile when
            toggled. Because the parent grid uses items-start, only this
            tile's grid cell grows; siblings stay short. */}
        {expanded && explanation && (
          <p className="mt-1.5 text-[10px] leading-snug text-slate-600 border-t border-slate-100 pt-1.5">
            {explanation}
          </p>
        )}
      </div>
    </div>
  );
}

// Top-5 categories by severity importance — used by CompactScoreBreakdown
// on the candidate card row where vertical space is limited.
const TOP_5_CATEGORIES = [
  'must_have_skills',
  'years_of_experience',
  'domain_relevance',
  'degree',
  'relevant_experience',
];

/**
 * Compact, name + bar + score only view of the score breakdown.
 *
 * Designed to live INSIDE a candidate card alongside other dense info
 * — fixed-narrow rows, tiny font, no severity tags, no explanation
 * paragraphs. Same data, same colour rules, fraction of the vertical
 * space. UNCHANGED from the previous version — only the modal-side
 * ScoreBreakdownSection got redesigned.
 *
 * Props:
 *   scoreBreakdown — the dict from the API
 *   topOnly        — when true, shows only the 5 highest-importance
 *                    categories (critical + important). Used on the
 *                    card view where vertical space is limited. The
 *                    full 8 always appears in the modal.
 *   columns        — 1 or 2. Default 2 (matches dense grid card layout).
 *                    Use 1 for the narrow "next to AI Insights" slot
 *                    in the list-view layout where horizontal width
 *                    is the constraint.
 *
 * Hidden when scoreBreakdown is null/empty so the card layout doesn't
 * suddenly grow a section labelled "Score Breakdown" with nothing
 * beneath it for candidates uploaded before this column existed.
 */
export function CompactScoreBreakdown({
  scoreBreakdown,
  topOnly = false,
  columns = 2,
  className = '',
}) {
  if (!scoreBreakdown || typeof scoreBreakdown !== 'object') return null;

  // Filter to the relevant subset, preserving canonical order.
  const order = topOnly ? TOP_5_CATEGORIES : CATEGORY_ORDER;
  const present = order.filter(
    (cat) => scoreBreakdown[cat] && typeof scoreBreakdown[cat] === 'object',
  );
  if (present.length === 0) return null;

  const gridCls = columns === 1
    ? 'grid grid-cols-1 gap-y-1'
    : 'grid grid-cols-2 gap-x-3 gap-y-1';

  return (
    <div className={`mt-2 ${className}`}>
      {/* Heading deliberately omitted — the bars + labels are
          self-explanatory at a glance, and the card already has the
          AI Insights button nearby for recruiters who want detail. */}
      <div className={gridCls}>
        {present.map((cat) => {
          const entry = scoreBreakdown[cat];
          const score = Number(entry?.score) || 0;
          const barPct = Math.max(0, Math.min(100, (score / 10) * 100));
          return (
            <div
              key={cat}
              className="flex items-center gap-1.5 min-w-0"
              title={entry?.explanation || ''}   // tooltip shows the full reasoning on hover
            >
              {/* Category name — truncates on overflow so the bar+score
                  on the right always stay visible. */}
              <span className="text-[10px] text-slate-700 truncate flex-1 min-w-0">
                {CATEGORY_LABELS_LONG[cat] || cat}
              </span>
              {/* Progress bar. Slightly wider in 1-column mode where
                  there's more horizontal room. */}
              <div className={
                'h-1 rounded-full bg-slate-100 overflow-hidden shrink-0 ' +
                (columns === 1 ? 'w-16' : 'w-12')
              }>
                <div
                  className={`h-full ${_barColor(score)}`}
                  style={{ width: `${barPct}%` }}
                />
              </div>
              {/* Score in tiny mono so the column widths stay aligned
                  even when scores go from 1 to 10 digits. */}
              <span className="text-[10px] font-semibold tabular-nums text-slate-700 w-5 text-right shrink-0">
                {score}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compact list-row card representing one scored category — used by the
 * `layout="list"` mode of ScoreBreakdownSection.
 *
 * Designed to stack vertically inside a narrow side column (e.g. the
 * right column of the company-side Resume Details popup) where the
 * 4-per-row tile grid wouldn't fit. Each card carries the full
 * description text, 2-line clamped with a Show more / Show less toggle
 * — so recruiters get both the numeric score AND the AI's reasoning
 * without leaving the card.
 *
 * Layout:
 *   ┌────────────────────────────────────┐
 *   │ Category Name        [Critical] 8/10│ ← single-line header
 *   │ ▓▓▓▓▓▓▓▓░░                          │ ← progress bar
 *   │ The AI's reasoning, 2 lines clamped │
 *   │ by default. "Show more" expands it… │
 *   │ Show more ▼                         │ ← only when text is long enough
 *   └────────────────────────────────────┘
 */
function ScoreListCard({ category, entry, expanded, onToggle }) {
  const label = CATEGORY_LABELS_LONG[category] || CATEGORY_LABELS[category] || category;
  const score = Number(entry?.score) || 0;
  const severity = entry?.severity || 'irrelevant';
  const severityCls = SEVERITY_STYLES[severity] || SEVERITY_STYLES.irrelevant;
  const severityLabel = SEVERITY_LABELS[severity] || severity;
  const explanation = (entry?.explanation || '').trim();
  const barPct = Math.max(0, Math.min(100, (score / 10) * 100));

  // Threshold for clamping. ~110 chars ≈ 2 lines at text-[10px] in a
  // ~400px column. Shorter than that already fits without a toggle.
  const SHOW_MORE_THRESHOLD = 110;
  const isLong = explanation.length > SHOW_MORE_THRESHOLD;

  return (
    <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
      {/* Single-row header — name · severity · score, pinned to one
          line so card heights stay predictable. */}
      <div className="flex items-center gap-1.5">
        <span
          className="text-[11px] font-semibold text-slate-800 truncate flex-1 min-w-0"
          title={label}
        >
          {label}
        </span>
        <span
          className={
            'inline-flex items-center text-[9px] font-semibold px-1.5 py-0 rounded border shrink-0 ' +
            severityCls
          }
          title={`This category is ${severityLabel.toLowerCase()} for this role.`}
        >
          {severityLabel}
        </span>
        <span className="text-[11px] font-bold tabular-nums text-slate-900 w-9 text-right shrink-0">
          {score}<span className="text-slate-400 font-medium">/10</span>
        </span>
      </div>

      {/* Thin progress bar — color-by-score same as everywhere else. */}
      <div className="mt-1.5 h-1 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full ${_barColor(score)} transition-[width] duration-300`}
          style={{ width: `${barPct}%` }}
        />
      </div>

      {/* Explanation paragraph + Show more/less toggle. Short
          explanations render fully without a button (no point cluttering
          the card with a toggle that reveals nothing). */}
      {explanation && (
        <div className="mt-1.5">
          <p
            className={
              'text-[10px] leading-snug text-slate-500 ' +
              (!expanded && isLong ? 'line-clamp-2' : '')
            }
          >
            {explanation}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={expanded}
              className="mt-0.5 text-[10px] font-semibold text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:underline"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Public component. Renders the 8-category scorecard in one of two
 * layouts:
 *
 *   layout="tiles" (default) — horizontal tile grid (4-per-row on xl).
 *     Used at the TOP of the AI Insights modal where there's full
 *     modal width to spread out and the recruiter scans severities at
 *     a glance.
 *
 *   layout="list" — compact vertical stack of cards, each with the
 *     full AI explanation visible (2-line clamped, "Show more"
 *     reveals the rest). Used in the right column of the company-side
 *     Resume Details popup where vertical real estate is more
 *     plentiful than horizontal width.
 *
 * Renders nothing when scoreBreakdown is null/empty — by design, since
 * old candidates uploaded before this feature shipped have no breakdown
 * stored and modals open identical to before for legacy data.
 */
export default function ScoreBreakdownSection({ scoreBreakdown, layout = 'tiles' }) {
  // Local state tracks which cards have their "Why?" / "Show more"
  // expanded. Object keyed by category so multiple cards can be open
  // simultaneously — lets recruiters compare explanations side by side.
  const [expandedTiles, setExpandedTiles] = useState({});

  // Hard guard — nothing to render. The parent modal still shows all its
  // existing AI cards below; we just don't add anything at the top.
  if (!scoreBreakdown || typeof scoreBreakdown !== 'object') return null;

  // Filter to categories the backend actually provided. Validator may have
  // dropped some, so don't assume all 8 are present. ORDER follows
  // CATEGORY_ORDER, not the dict's iteration order.
  const presentCategories = CATEGORY_ORDER.filter(
    (cat) => scoreBreakdown[cat] && typeof scoreBreakdown[cat] === 'object'
  );

  if (presentCategories.length === 0) return null;

  const toggleTile = (cat) =>
    setExpandedTiles((prev) => ({ ...prev, [cat]: !prev[cat] }));

  return (
    <div className="break-inside-avoid">
      {/* Section header — small uppercase label + n/8 count chip. The
          count helps recruiters spot when the AI failed validation on
          a category (e.g. "6/8" means 2 categories dropped). */}
      <div className="flex items-center justify-between mb-2 px-0.5">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
          Score Breakdown
        </h3>
        <span className="text-[10px] font-semibold text-slate-400 tabular-nums">
          {presentCategories.length}/8
        </span>
      </div>

      {layout === 'list' ? (
        /* LIST layout — vertical stack of compact cards, each with its
           own description and Show-more toggle. */
        <div className="flex flex-col gap-1.5">
          {presentCategories.map((cat) => (
            <ScoreListCard
              key={cat}
              category={cat}
              entry={scoreBreakdown[cat]}
              expanded={!!expandedTiles[cat]}
              onToggle={() => toggleTile(cat)}
            />
          ))}
        </div>
      ) : (
        /* TILES layout (default) — responsive tile grid:
              sm  → 1 col  (narrowest, e.g. mobile)
              md  → 2 cols (tablet)
              lg  → 3 cols (medium desktop)
              xl  → 4 cols (full modal width on desktop)
           items-start so an expanded tile's row grows independently —
           siblings don't stretch to match the tallest. */
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 items-start">
          {presentCategories.map((cat) => (
            <ScoreTile
              key={cat}
              category={cat}
              entry={scoreBreakdown[cat]}
              expanded={!!expandedTiles[cat]}
              onToggle={() => toggleTile(cat)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
